import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Avatar component for web
 *
 * Displays user profile image or initials with optional online status indicator
 */
const Avatar = ({
  src,
  alt = '',
  name,
  size = 'md',
  fallback,
  online = false,
  className = '',
  ...props
}) => {
  const [imageError, setImageError] = useState(false);

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const sizeClasses = {
    sm: 'avatar-sm',
    md: 'avatar-md',
    lg: 'avatar-lg',
    xl: 'avatar-xl',
    '2xl': 'avatar-2xl',
  };

  const onlineIndicatorSizes = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4',
    '2xl': 'w-5 h-5',
  };

  const avatarClasses = [
    'avatar',
    sizeClasses[size],
    className,
  ].filter(Boolean).join(' ');

  const showImage = src && !imageError;
  const showInitials = !showImage && name;
  const showFallback = !showImage && !showInitials && fallback;

  return (
    <div className="relative inline-block">
      <div className={avatarClasses} {...props}>
        {showImage && (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        )}

        {showInitials && (
          <span>{getInitials(name)}</span>
        )}

        {showFallback && fallback}

        {!showImage && !showInitials && !showFallback && (
          <span>?</span>
        )}
      </div>

      {online && (
        <span
          className={`absolute bottom-0 right-0 block rounded-full bg-success-500 ring-2 ring-white ${onlineIndicatorSizes[size]}`}
          aria-label="Online"
        />
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl']),
  fallback: PropTypes.node,
  online: PropTypes.bool,
  className: PropTypes.string,
};

export default Avatar;
